import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import LeadManagement from './LeadManagement';
import ChatSystem from './ChatSystem';
import FollowUpDashboard from './FollowUpDashboard';
import NotificationBell from './NotificationBell';
import {
  HomeIcon,
  ChartBarIcon,
  PhoneArrowUpRightIcon,
  Cog6ToothIcon,
  ChatBubbleLeftRightIcon,
  PresentationChartLineIcon,
} from '@heroicons/react/24/outline';

const DashboardLead = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('leads');
  const [showChat, setShowChat] = useState(false);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [leadManagementTab, setLeadManagementTab] = useState('dashboard'); // For LeadManagement internal tabs

  const handleChatToggle = () => {
    setShowChat(prev => !prev);
  };

  const handleMarkAsRead = (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  const handleMarkAllAsRead = () => {
    setNotifications([]);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header with Notification Bell */}
      {/* <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-900">CRM Dashboard</h1>
            </div>
            <div className="flex items-center space-x-4">
              <NotificationBell 
                notifications={notifications}
                followUps={followUps}
                onMarkAsRead={handleMarkAsRead}
                onMarkAllAsRead={handleMarkAllAsRead}
              />
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {user?.name?.charAt(0) || 'U'}
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-700">{user?.name || 'User'}</span>
              </div>
            </div>
          </div>
        </div>
      </header> */}

      {/* Main Content */}
      <main className="py-4 px-2">
        {/* Desktop Navigation - Hidden on mobile, original layout and color scheme */}
        <div className="hidden md:block bg-white/80 backdrop-blur-md shadow-xl rounded-2xl mb-4 border border-gray-200/50">
          <div className="border-b border-gray-200/50">
            <div className="flex justify-between items-center px-4 py-2 flex-wrap gap-2">
              {/* Left: Lead Management Tab */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setActiveTab('leads')}
                  className={`py-2 px-3 font-semibold text-sm transition-all duration-200 rounded ${
                    activeTab === 'leads'
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <PresentationChartLineIcon className="h-4 w-4" />
                  Lead Management
                </button>
              </div>

              {/* Right: Navigation Buttons for LeadManagement */}
              {activeTab === 'leads' && (
                <div className="flex items-center flex-wrap gap-1.5">
                  <button
                    onClick={() => setLeadManagementTab('dashboard')}
                    className={`px-3 py-1.5 rounded-lg font-semibold text-sm transition-all duration-200 ${leadManagementTab === 'dashboard'
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    <HomeIcon className="h-4 w-4 inline-block mr-1 -mt-0.5" />
                    Home
                  </button>

                  <button
                    onClick={() => setLeadManagementTab('analytics')}
                    className={`px-3 py-1.5 rounded-lg font-semibold text-sm transition-all duration-200 ${leadManagementTab === 'analytics'
                      ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    <ChartBarIcon className="h-4 w-4 inline-block mr-1 -mt-0.5" />
                    Analytics
                  </button>

                  <button
                    onClick={() => setLeadManagementTab('followups')}
                    className={`px-3 py-1.5 rounded-lg font-semibold text-sm transition-all duration-200 ${leadManagementTab === 'followups'
                      ? 'bg-gradient-to-r from-pink-600 to-rose-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    <PhoneArrowUpRightIcon className="h-4 w-4 inline-block mr-1 -mt-0.5" />
                    Follow-ups
                  </button>

                  <button
                    onClick={() => setLeadManagementTab('settings')}
                    className={`px-3 py-1.5 rounded-lg font-semibold text-sm transition-all duration-200 ${leadManagementTab === 'settings'
                      ? 'bg-gradient-to-r from-purple-600 to-violet-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    <Cog6ToothIcon className="h-4 w-4 inline-block mr-1 -mt-0.5" />
                    Settings
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Navigation - Visible only on mobile, glassmorphism blue style */}
        <div className="md:hidden bg-gradient-to-br from-blue-700/90 via-blue-800/85 to-indigo-900/90 backdrop-blur-md text-white shadow-[0_8px_32px_rgba(37,99,235,0.35)] rounded-2xl mb-4 border border-white/20 p-4">
          <div className="flex flex-col gap-3.5">
            {/* Header / Title */}
            <div className="flex items-center justify-between border-b border-white/20 pb-2.5">
              <span className="text-base font-extrabold tracking-wide flex items-center gap-2 text-white drop-shadow-sm">
                <PresentationChartLineIcon className="h-5 w-5" />
                Lead Management
              </span>
            </div>

            {/* Buttons Grid */}
            {activeTab === 'leads' && (
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setLeadManagementTab('dashboard')}
                  className={`px-3 py-2.5 rounded-xl font-bold text-xs tracking-wide transition-all duration-200 text-center border backdrop-blur-sm ${
                    leadManagementTab === 'dashboard'
                      ? 'bg-white/95 text-blue-700 border-white shadow-lg shadow-blue-900/30'
                      : 'bg-white/10 text-white border-white/30 hover:bg-white/20 hover:border-white/50 drop-shadow-sm'
                  }`}
                >
                  <span className="flex flex-col items-center gap-1">
                    <HomeIcon className="h-5 w-5" />
                    <span>Home</span>
                  </span>
                </button>

                <button
                  onClick={() => setLeadManagementTab('analytics')}
                  className={`px-3 py-2.5 rounded-xl font-bold text-xs tracking-wide transition-all duration-200 text-center border backdrop-blur-sm ${
                    leadManagementTab === 'analytics'
                      ? 'bg-white/95 text-emerald-700 border-white shadow-lg shadow-blue-900/30'
                      : 'bg-white/10 text-white border-white/30 hover:bg-white/20 hover:border-white/50 drop-shadow-sm'
                  }`}
                >
                  <span className="flex flex-col items-center gap-1">
                    <ChartBarIcon className="h-5 w-5" />
                    <span>Analytics</span>
                  </span>
                </button>

                <button
                  onClick={() => setLeadManagementTab('followups')}
                  className={`px-3 py-2.5 rounded-xl font-bold text-xs tracking-wide transition-all duration-200 text-center border backdrop-blur-sm ${
                    leadManagementTab === 'followups'
                      ? 'bg-white/95 text-rose-700 border-white shadow-lg shadow-blue-900/30'
                      : 'bg-white/10 text-white border-white/30 hover:bg-white/20 hover:border-white/50 drop-shadow-sm'
                  }`}
                >
                  <span className="flex flex-col items-center gap-1">
                    <PhoneArrowUpRightIcon className="h-5 w-5" />
                    <span>Follow-ups</span>
                  </span>
                </button>

                <button
                  onClick={() => setLeadManagementTab('settings')}
                  className={`px-3 py-2.5 rounded-xl font-bold text-xs tracking-wide transition-all duration-200 text-center border backdrop-blur-sm ${
                    leadManagementTab === 'settings'
                      ? 'bg-white/95 text-purple-700 border-white shadow-lg shadow-blue-900/30'
                      : 'bg-white/10 text-white border-white/30 hover:bg-white/20 hover:border-white/50 drop-shadow-sm'
                  }`}
                >
                  <span className="flex flex-col items-center gap-1">
                    <Cog6ToothIcon className="h-5 w-5" />
                    <span>Settings</span>
                  </span>
                </button>

                <button
                  onClick={handleChatToggle}
                  className={`col-span-2 px-3 py-3 rounded-xl font-bold text-xs tracking-wide transition-all duration-200 flex items-center justify-center gap-2 border backdrop-blur-sm ${
                    showChat
                      ? 'bg-white/95 text-indigo-700 border-white shadow-lg shadow-blue-900/30'
                      : 'bg-white/10 text-white border-white/30 hover:bg-white/20 hover:border-white/50 drop-shadow-sm'
                  }`}
                >
                  <ChatBubbleLeftRightIcon className="h-5 w-5" />
                  <span>Messages</span>
                  {unreadChatCount > 0 && (
                    <span className="bg-red-500 text-white text-[9px] rounded-full h-4 w-4 flex items-center justify-center font-bold">
                      {unreadChatCount > 99 ? '99+' : unreadChatCount}
                    </span>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'leads' && (
            <LeadManagement 
              activeTab={leadManagementTab}
              setActiveTab={setLeadManagementTab}
            />
          )}
          {activeTab === 'followups' && (
            <FollowUpDashboard 
              onNotificationsUpdate={setNotifications}
              onFollowUpsUpdate={setFollowUps}
            />
          )}
        </div>
      </main>

      {/* Chat System */}
      <ChatSystem
        currentUser={user}
        isOpen={showChat}
        onToggle={handleChatToggle}
        hideFloatingButton={true}
        onUnreadCountChange={setUnreadChatCount}
      />
    </div>
  );
};

export default DashboardLead;
